import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  AttendanceSchema,
  AttendanceCreateInputSchema,
  AttendanceUpdateInputSchema,
  type Attendance,
  type AttendanceCreateInput,
  type AttendanceUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `attendance` (présence / émargement, org-wide).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`AttendanceCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit
 * jamais un document pour découvrir ensuite qu'il est invalide. Les liens vers
 * d'autres familles (contact, event, booking, shift, registration, credit,
 * policy) sont de simples ids.
 */

export type { AttendanceCreateInput };

/** Nom de la collection Firestore de la famille attendance. */
export const ATTENDANCES_COLLECTION = 'attendances';

/**
 * Repository typé pour la famille `attendance`.
 * Toute lecture est validée par `AttendanceSchema`, toute écriture par
 * `AttendanceCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface AttendanceRepository {
  create(input: AttendanceCreateInput): Promise<Attendance>;
  list(options?: ListOptions): Promise<Attendance[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Attendance | null>;
  update(id: string, patch: AttendanceUpdateInput): Promise<Attendance>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Attendance>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Attendance>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Attendance>;
}

// Relecture : le converter generique convertit deja TOUS les Timestamps
// (standard et metier) en Date, cf. standard-fields.ts.
const attendanceConverter = createConverter<Attendance>((raw: DocumentData) => AttendanceSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createAttendanceRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): AttendanceRepository {
  const col = collection(db, ATTENDANCES_COLLECTION).withConverter(attendanceConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Attendance>(db, col, ATTENDANCES_COLLECTION, getActorId);

  return {
    async create(input: AttendanceCreateInput): Promise<Attendance> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = AttendanceCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.eventId !== undefined) optional['eventId'] = fields.eventId;
      if (fields.bookingId !== undefined) optional['bookingId'] = fields.bookingId;
      if (fields.shiftId !== undefined) optional['shiftId'] = fields.shiftId;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.checkInAt !== undefined) optional['checkInAt'] = fields.checkInAt;
      if (fields.checkOutAt !== undefined) optional['checkOutAt'] = fields.checkOutAt;
      if (fields.signedBy !== undefined) optional['signedBy'] = fields.signedBy;
      if (fields.registrationId !== undefined) optional['registrationId'] = fields.registrationId;
      if (fields.creditId !== undefined) optional['creditId'] = fields.creditId;
      if (fields.policyId !== undefined) optional['policyId'] = fields.policyId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        contactId: fields.contactId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Attendance);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit (checkInAt/checkOutAt sont déjà
      // des Date issues de l'entrée validée).
      return AttendanceSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Attendance[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Attendance | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: AttendanceUpdateInput): Promise<Attendance> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = AttendanceUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, ATTENDANCES_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`attendance ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
